import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Spin, Alert, message, Button, Collapse, Checkbox } from 'antd';
import { ShareAltOutlined } from '@ant-design/icons';
import { useWindowWidth } from '@react-hook/window-size';
import DOMPurify from 'dompurify';
import { serverFetch } from '../../server';
import { closePaymentWindow, navigateToPayment, preparePaymentWindow, shouldOpenPaymentWindow } from '../../extensions/payment-window';
import { getUtmTagId } from '../../extensions/utils';
import { companyAuthMode, productType } from '../../extensions/helper';
import Auth from '../auth/auth';
import GuestContact from '../auth/guest-contact';
import Subscription from '../catalog/subscription';
import Certificate from '../catalog/certificate';
import Booking from '../catalog/booking';
import { PayButtons } from '../../controls/index';
import './action.css';

const { Panel } = Collapse;

const Action = () => {
    const { id } = useParams();
    const history = useHistory();
    const location = useLocation();
    const user = useSelector((s) => s.user);
    const company = useSelector((s) => s.company);
    const onlyWidth = useWindowWidth();
    const [offer, setOffer] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [showSignin, setShowSignin] = useState(false);
    const [showGuest, setShowGuest] = useState(false);
    const [guestUser, setGuestUser] = useState(null);
    const [paying, setPaying] = useState(false);
    const [pendingPaymentSystem, setPendingPaymentSystem] = useState(null);
    const [currentItem, setCurrentItem] = useState();
    const [currentRents, setCurrentRents] = useState([]);
    const [autopay, setAutopay] = useState(false);
    const [cond1, setCond1] = useState(false);
    const [cond2, setCond2] = useState(false);
    const [cond3, setCond3] = useState(true);
    const [condF, setCondF] = useState(true);
    const paymentWindowRef = useRef(null);

    // Обработка реферального параметра при загрузке
    useEffect(() => {
        if (!id) return;

        const searchParams = new URLSearchParams(location.search);
        const referalId = searchParams.get('ref');

        if (referalId) {
            // Сохраняем реферальный ID в localStorage
            const storageKey = `action_${id}`;
            localStorage.setItem(storageKey, referalId);
            console.log(`Реферал сохранен: ${storageKey} = ${referalId}`);

            // Удаляем параметр ref из URL
            searchParams.delete('ref');
            const newSearch = searchParams.toString();
            const newUrl = newSearch ? `${location.pathname}?${newSearch}` : location.pathname;
            history.replace(newUrl);
        }
    }, [id, location.search, location.pathname, history]);

    // Загрузка данных акции
    useEffect(() => {
        if (!id) {
            setError(true);
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(false);

        serverFetch(`offer/${id}`, { method: 'GET' }, user)
            .then((data) => {
                if (data) {
                    setOffer(data);
                    setAutopay(!!data.autopay);
                    setCond3(!data.autopay);
                    setCond1(false);
                    setCond2(false);
                    setCondF(data.productType !== productType.Certificate);
                    setCurrentItem({
                        productId: data.productId,
                        productType: data.productType,
                        productDuration: data.productDuration,
                        price: data.syma,
                        totalPrice: data.productType === productType.Booking ? null : data.syma,
                    });
                    setCurrentRents([]);
                } else {
                    setError(true);
                }
                setLoading(false);
            })
            .catch((e) => {
                console.error('Ошибка загрузки акции:', e);
                setError(true);
                setLoading(false);
            });
    }, [id, user]);

    const catalogItem = useMemo(
        () =>
            offer && {
                id: 0,
                catalogId: 0,
                productId: offer.productId,
                name: offer.productName || offer.name,
                price: offer.syma,
                productType: offer.productType,
                productDuration: offer.productDuration,
                autopay: offer.autopay,
                qtyPromocodes: 0,
            },
        [offer]
    );

    const onItemChange = (data) => {
        setCurrentItem((prev) => ({ ...(prev || {}), ...data, productType: offer?.productType }));
    };

    // Функция для выполнения оплаты (мемоизирована для корректной работы в useEffect)
    const processPayment = useCallback((paymentSystem, actor = user) => {
        setPaying(true);

        // Получаем referalId из localStorage
        const storageKey = `action_${id}`;
        const referalId = localStorage.getItem(storageKey);

        const saleData = {
            id: parseInt(id),
            referalId: referalId ? parseInt(referalId) : null,
            paymentSystem: paymentSystem,
            autopay,
            utmId: getUtmTagId(),
            date: currentItem?.date,
            roomId: currentItem?.roomId,
            startMin: currentItem?.startMin,
            qtyClients: currentItem?.qtyClients,
            receiverName: currentItem?.receiverName,
            receiverEmail: currentItem?.receiverEmail,
            description: currentItem?.Description || currentItem?.description,
            signText: currentItem?.signText,
            imageId: currentItem?.imageId,
            rents: (currentRents || [])
                .filter((item) => item.productId && item.size)
                .map((item) => ({ productId: item.productId, size: item.size })),
        };

        console.log('Создание продажи с данными:', saleData);

        serverFetch('offer/Sale', { method: 'POST', bodyData: saleData }, actor)
            .then((paymentUrl) => {
                const opened = navigateToPayment(paymentUrl, paymentSystem, paymentWindowRef.current);
                paymentWindowRef.current = null;
                if (!opened) message.error('Разрешите всплывающие окна для перехода к оплате');
            })
            .catch((e) => {
                closePaymentWindow(paymentWindowRef.current);
                paymentWindowRef.current = null;
                setPaying(false);
                const errorMessage = e.UserMessage || 'Ошибка при создании оплаты';
                message.error(errorMessage);
                console.error('Ошибка оплаты акции:', e);
            });
    }, [id, user, currentItem, currentRents, autopay]);

    // Автоматическое продолжение оплаты после авторизации
    useEffect(() => {
        if (user && pendingPaymentSystem && !showSignin) {
            // Пользователь авторизовался и у нас есть сохраненная платежная система
            const paymentSystem = pendingPaymentSystem;
            setPendingPaymentSystem(null); // Очищаем сохраненную платежную систему
            
            // Запускаем оплату
            processPayment(paymentSystem);
        }
    }, [user, pendingPaymentSystem, showSignin, processPayment]);

    const handlePay = (paymentSystem) => {
        const paymentWindow = preparePaymentWindow(paymentSystem);
        if (shouldOpenPaymentWindow(paymentSystem) && !paymentWindow) {
            message.error('Разрешите всплывающие окна для перехода к оплате');
            return;
        }
        paymentWindowRef.current = paymentWindow;

        // Проверяем авторизацию
        if (user) {
            processPayment(paymentSystem);
            return;
        }
        if (guestUser) {
            processPayment(paymentSystem, guestUser);
            return;
        }
        // Сохраняем платежную систему для автоматического продолжения после авторизации
        setPendingPaymentSystem(paymentSystem);
        if (company?.authMode === companyAuthMode.NoAuth) setShowGuest(true);
        else setShowSignin(true);
    };

    const onGuestSuccess = (session) => {
        setGuestUser(session);
        setShowGuest(false);
        if (pendingPaymentSystem) {
            const paymentSystem = pendingPaymentSystem;
            setPendingPaymentSystem(null);
            processPayment(paymentSystem, session);
        }
    };

    const onGuestClose = () => {
        setShowGuest(false);
        if (!user) {
            setPendingPaymentSystem(null);
            closePaymentWindow(paymentWindowRef.current);
            paymentWindowRef.current = null;
        }
    };

    const handleAuthClose = () => {
        setShowSignin(false);
        // Если авторизация не состоялась, очищаем pendingPaymentSystem
        if (!user) {
            setPendingPaymentSystem(null);
            closePaymentWindow(paymentWindowRef.current);
            paymentWindowRef.current = null;
        }
    };

    const handleShare = () => {
        if (!user || !user.clientId) {
            message.warning('Для создания реферальной ссылки необходимо авторизоваться');
            return;
        }

        // Формируем URL с реферальным параметром
        const currentUrl = window.location.origin + window.location.pathname;
        const referralUrl = `${currentUrl}?ref=${user.clientId}`;

        // Копируем в буфер обмена
        navigator.clipboard.writeText(referralUrl)
            .then(() => {
                message.success('Ссылка скопирована в буфер обмена!');
            })
            .catch((err) => {
                console.error('Ошибка копирования в буфер:', err);
                message.error('Не удалось скопировать ссылку');
            });
    };

    if (loading) {
        return (
            <div className="action-container">
                <div className="action-loading">
                    <Spin size="large" tip="Загрузка акции..." />
                </div>
            </div>
        );
    }

    if (error || !offer) {
        return (
            <div className="action-container">
                <div className="action-error">
                    <Alert
                        message="Акция сейчас не проводится"
                        type="warning"
                        showIcon
                    />
                </div>
            </div>
        );
    }

    const isMobile = onlyWidth < 768;
    const offerAutopay = company && company.offerAutopay ? company.offerAutopay : process.env.REACT_APP_OFFER_AUTOPAY;
    const offerPayment = company && company.offerPayment ? company.offerPayment : process.env.REACT_APP_OFFER_PAYMENT;
    const divExtraInfo = <a href={offerPayment} target='_blank' rel="noreferrer">условиями</a>;

    const productForm = catalogItem ? (
        offer.productType === productType.Subscription ? (
            <Subscription item={catalogItem} setCurrentItem={onItemChange} skipCommission />
        ) : offer.productType === productType.Certificate ? (
            <Certificate item={catalogItem} setCurrentItem={onItemChange} setCond={setCondF} skipCommission />
        ) : offer.productType === productType.Booking ? (
            <Booking
                item={catalogItem}
                setCurrentRents={setCurrentRents}
                setCurrentItem={onItemChange}
                fixedPrice={offer.syma}
                fixedQtyClients={offer.qty > 0 ? offer.qty : 1}
                bookFrom={offer.bookFrom}
                bookTo={offer.bookTo}
            />
        ) : null
    ) : null;

    const isBooking = offer.productType === productType.Booking;
    const isCertificate = offer.productType === productType.Certificate;
    const displayPrice = isBooking
        ? (currentItem?.totalPrice ?? offer.syma)
        : offer.syma;

    const DisablePayment =
        paying ||
        !cond1 ||
        !cond2 ||
        !condF ||
        (autopay && !cond3) ||
        (isBooking && (currentItem?.totalPrice == null || currentItem?.startMin == null)) ||
        (isCertificate && !currentItem?.receiverEmail);

    return (
        <>
            <div className="action-container">
                <div className="action-content">
                    {company && company.catalogBackgroundImage && (
                        <div 
                            className="action-background"
                            style={{ backgroundImage: `url("${company.catalogBackgroundImage}")` }}
                        />
                    )}
                    <div 
                        className="action-body"
                        dangerouslySetInnerHTML={{ 
                            __html: DOMPurify.sanitize(offer.body, {
                                USE_PROFILES: { html: true }
                            }) 
                        }}
                    />
                    
                    {/* Форма покупки: подписка / запись / сертификат */}
                    {productForm && <div className="action-product-form">{productForm}</div>}

                    {/* Кнопки оплаты */}
                    <div className="action-payment">
                        <div className="action-terms">
                            <Collapse size='small' accordion>
                                <Panel header='Условия платежа' key='1'>
                                    Соглашаясь с {divExtraInfo} платежа, вы соглашаетесь с составом и стоимостью заказа, а также с суммой комиссии,
                                    если она указана в заказе. Пожалуйста, внимательно ознакомьтесь с наименованием состава заказа,
                                    стоимостью заказа и описанием предоставления заказа.
                                </Panel>
                                <Panel header='Условия аннуляции заказа' key='2'>
                                    Для аннуляции оплаченного заказа вы можете обратиться с Заявлением на возврат суммы оплаты на вашу банковскую карту к Поставщику услуг, указанному в чеке, или на e-mail Компании Проффит: inbox@fitnessoft.ru Аннуляция заказа и возврат оплаты возможны до начала использования заказа. В случае  аннулирования заказа Поставщик услуг имеет право взимать с клиента сервисный сбор в размере фактически понесенных расходов. Сервисный сбор включается в стоимость заказа. Размер сервисного сбора не может превышать 5% от оплаченной клиентом суммы. При аннуляции заказа до момента использования заказа денежные средства будут возвращены на банковскую карту,  которой была произведена оплата услуги в течение 10 рабочих дней с момента получения  Поставщиком услуг Зявления на возврат. В случае аннуляции заказа после начала использования заказа для возврата денежных средств вам необходимо обратиться непосредственно к Поставщику услуг для расчёта и возврата  остатка стоимости заказа.
                                </Panel>
                                {autopay ? (
                                    <Panel header="Условия сервиса “Автоплатеж”" key="3">
                                        Условия пользования сервисом «Автоплатеж» в соответствии со ст.435 ГК РФ являются офертой, адресованной Пользователю. Пользователь, как держатель банковской карты,
                                        производя оплату соглашается с <a href={offerAutopay} target='_blank' rel="noreferrer">Условиями</a>
                                    </Panel>
                                ) : null}
                            </Collapse>
                            <div>
                                <Checkbox onChange={(e) => setCond1(e.target.checked)} checked={cond1}>
                                    Я согласен с условиями платежа
                                </Checkbox>
                            </div>
                            <div>
                                <Checkbox onChange={(e) => setCond2(e.target.checked)} checked={cond2}>
                                    Я согласен с условиями аннуляции заявки
                                </Checkbox>
                            </div>
                            {autopay ? (
                                <Checkbox onChange={(e) => setCond3(e.target.checked)} checked={cond3}>
                                    Я согласен с сохранением данных карты и условиями сервиса "Автоплатеж"
                                </Checkbox>
                            ) : null}
                        </div>

                        <div className="action-payment-info">
                            {displayPrice > 0 && (
                                <div className="action-price">
                                    <span className="action-price-label">Стоимость:</span>
                                    <span className="action-price-value">
                                        {Number(displayPrice).toLocaleString('ru-RU', { 
                                            minimumFractionDigits: 2, 
                                            maximumFractionDigits: 2 
                                        })} ₽
                                    </span>
                                </div>
                            )}
                        </div>
                        
                        <PayButtons
                            company={company}
                            isMobile={isMobile}
                            onPay={handlePay}
                            disablePayment={DisablePayment}
                            autopay={autopay}
                            price={displayPrice}
                            onBack={undefined}
                        />

                        {/* Блок "Поделиться" для авторизованных пользователей */}
                        {user && user.clientId && (
                            <div className="action-share">
                                <div className="action-share-text">Поделитесь с друзьями акцией</div>
                                <Button type="default" icon={<ShareAltOutlined />} onClick={handleShare} size="large" className="action-share-btn">Поделиться</Button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            
            {showSignin && <Auth onClose={handleAuthClose} />}
            {showGuest && <GuestContact onClose={onGuestClose} onSuccess={onGuestSuccess} />}
        </>
    );
};

export default Action;
